import React, { useState, useEffect } from 'react';
import { AWS_CONFIG } from '../config/aws';
import { formatKoreanPhone, digitsOnly } from '../utils/phone';
import useBackButtonClose from '../utils/useBackButtonClose';

// 히어로 '중고차 구매' 박스 클릭 시 열리는 문의 폼 모달.
// 이름·연락처·차종·가격대를 받아 POST /dealer-inquiries (kind='used-car').
// 차종/가격대는 자유 입력(선택). 연락처는 휴대폰 형식 검증.

const EMPTY = { name: '', phone: '', carType: '', priceRange: '' };

export default function UsedCarInquiryModal({ open, onClose }) {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  // Reset state whenever the modal is (re)opened.
  useEffect(() => {
    if (open) { setForm(EMPTY); setDone(false); setError(''); }
  }, [open]);

  // Close on ESC for accessibility.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // 모바일 뒤로가기로 모달만 닫기(SPA 이탈 방지).
  useBackButtonClose(open, onClose);

  if (!open) return null;

  const setField = (k) => (e) => {
    const v = k === 'phone' ? formatKoreanPhone(e.target.value) : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim()) { setError('이름을 입력해주세요.'); return; }
    if (digitsOnly(form.phone).length !== 11 || !digitsOnly(form.phone).startsWith('010')) {
      setError('휴대폰 번호를 정확히 입력해주세요. (010-XXXX-XXXX)');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${AWS_CONFIG.apiUrl}/dealer-inquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kind: 'used-car',
          name: form.name.trim(),
          phone: form.phone.trim(),
          carType: form.carType.trim(),
          priceRange: form.priceRange.trim(),
        }),
      });
      if (!res.ok) {
        // Surface the backend's error message when present, else fall back to the status code.
        let msg = `접수 실패 (${res.status})`;
        try { const b = await res.json(); if (b?.error) msg = b.error; } catch { /* keep default */ }
        throw new Error(msg);
      }
      setDone(true);
    } catch (err) {
      setError(err.message || '접수에 실패했습니다. 잠시 후 다시 시도해주세요.');
    }
    setSubmitting(false);
  };

  return (
    <div onClick={onClose} style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={panel}>
        <button onClick={onClose} aria-label="닫기" style={closeBtn}>×</button>
        {done ? (
          <div style={{ textAlign: 'center', padding: '12px 0' }}>
            <div style={{ fontSize: '2.4rem', marginBottom: 8 }}>✅</div>
            <h3 style={{ margin: '0 0 8px' }}>문의가 접수되었습니다</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem' }}>
              담당자가 입력하신 연락처로 곧 연락드리겠습니다.
            </p>
            <button className="btn btn-primary" onClick={onClose} style={{ marginTop: 16 }}>
              확인
            </button>
          </div>
        ) : (
          <form onSubmit={submit}>
            <h3 style={{ margin: '0 0 4px' }}>🚗 중고차 구매 문의</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.88rem', margin: '0 0 16px' }}>
              원하시는 차량 정보를 남겨주시면 맞는 매물을 찾아 연락드립니다.
            </p>
            <Field label="이름" value={form.name} onChange={setField('name')} placeholder="홍길동" required />
            <Field label="연락처" value={form.phone} onChange={setField('phone')} placeholder="010-1234-5678" inputMode="numeric" required />
            <Field label="희망 차종" value={form.carType} onChange={setField('carType')} placeholder="예: 아반떼, SUV, 무관" />
            <Field label="희망 가격대" value={form.priceRange} onChange={setField('priceRange')} placeholder="예: 1500만원대, 2천 이하" />
            {error && <p style={{ color: '#ff5252', fontSize: '0.85rem', margin: '4px 0 12px' }}>{error}</p>}
            <button type="submit" className="btn btn-primary" disabled={submitting}
              style={{ width: '100%', padding: '12px', fontSize: '1rem' }}>
              {submitting ? '접수 중...' : '문의 보내기'}
            </button>
            <p style={{ color: 'var(--text-dim)', fontSize: '0.75rem', marginTop: 10, lineHeight: 1.5 }}>
              제출 시 <a href="/privacy" target="_blank" rel="noreferrer" style={{ color: 'var(--primary)' }}>개인정보 수집·이용</a>에
              동의하는 것으로 간주합니다. (문의 응대 목적, 보관 후 파기)
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, ...props }) {
  return (
    <label style={{ display: 'block', marginBottom: 12 }}>
      <span style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: 4 }}>
        {label}{props.required && <span style={{ color: '#ff5252' }}> *</span>}
      </span>
      <input {...props} style={{
        width: '100%', boxSizing: 'border-box', padding: '10px 12px',
        borderRadius: 8, border: '1px solid var(--border)',
        background: 'var(--surface)', color: 'var(--text)', fontSize: '0.95rem',
      }} />
    </label>
  );
}

const overlay = {
  position: 'fixed', inset: 0, zIndex: 1000,
  background: 'rgba(0,0,0,0.6)', display: 'flex',
  alignItems: 'center', justifyContent: 'center', padding: 16,
};
const panel = {
  position: 'relative', width: '100%', maxWidth: 420,
  background: 'var(--bg)', borderRadius: 16, padding: 24,
  border: '1px solid var(--border)', maxHeight: '90vh', overflowY: 'auto',
};
const closeBtn = {
  position: 'absolute', top: 12, right: 14,
  background: 'none', border: 'none', color: 'var(--text-muted)',
  fontSize: '1.6rem', lineHeight: 1, cursor: 'pointer',
};
